/**
 * Hotel & Lodging staffing structure (5-star benchmark).
 *
 * Roles are organized by department and tagged with a priority
 * (essential / recommended / optional). Salary ranges are *not* hardcoded
 * here — they are derived dynamically from the per-city luxury hospitality
 * dataset in `cityStaffData.ts` so that switching the city updates every
 * department in the staffing planner.
 *
 * Each role declares either:
 *   - `anchorRole`: a role name that already exists in `cityStaffData` for
 *     the selected city. The annual range is taken directly from that role.
 *   - `anchorRole` + `multiplier`: scale the anchor's range up or down to
 *     approximate roles not in the F&B dataset (e.g. General Manager,
 *     Front Desk Agent, Spa Director).
 *
 * Multipliers were calibrated against typical 5-star hotel pay bands
 * relative to Executive Chef / Restaurant Manager benchmarks across
 * NYC, London, Dubai and Singapore (2025/2026 market data).
 */

import { CITY_STAFF_DATA, getCityStaffProfile, type CityRoleSalary, type CityStaffProfile } from "./cityStaffData";

export type HotelRolePriority = "essential" | "recommended" | "optional";

export interface HotelRoleDef {
  /** Display name. */
  name: string;
  priority: HotelRolePriority;
  /** Short helper line shown under the role. */
  description: string;
  /** Name of a role in `cityStaffData` used as the salary anchor. */
  anchorRole: string;
  /** Multiplier applied to the anchor role's annual range. Defaults to 1. */
  multiplier?: number;
  /** Optional suffix appended to the rendered range (e.g. "+service charge"). */
  suffix?: string;
}

export interface HotelDepartmentDef {
  id: string;
  /** Emoji + label as shown in the brief. */
  label: string;
  /** Short department description. */
  description: string;
  roles: HotelRoleDef[];
}

/**
 * Anchor fallback chain — if the city data doesn't include the preferred
 * anchor role, we walk this list until we find one that exists. Every city
 * in `CITY_STAFF_DATA` includes Executive Chef, so the chain always
 * terminates safely.
 */
const ANCHOR_FALLBACKS: Record<string, string[]> = {
  "Executive Chef": ["Executive Chef"],
  "F&B Director": ["F&B Director", "Executive Chef"],
  "Restaurant Manager": ["Restaurant Manager", "F&B Director", "Executive Chef"],
  "Sous Chef": ["Sous Chef", "Executive Chef"],
  "Chef de Partie": ["Chef de Partie", "Sous Chef", "Executive Chef"],
  "Commis": ["Commis", "Chef de Partie", "Sous Chef", "Executive Chef"],
  "Pastry Chef": ["Pastry Chef", "Executive Chef"],
  "Pastry Sous Chef": ["Pastry Sous Chef", "Pastry Chef", "Sous Chef", "Executive Chef"],
  "Maître d'": ["Maître d'", "Restaurant Manager", "Executive Chef"],
  "Sommelier": ["Sommelier", "Restaurant Manager", "Executive Chef"],
  "Head Bartender": ["Head Bartender", "Restaurant Manager", "Executive Chef"],
  "Bartender": ["Bartender", "Head Bartender", "Restaurant Manager", "Executive Chef"],
  "Waiter": ["Waiter", "Commis", "Chef de Partie", "Executive Chef"],
  "Senior Waiter": ["Senior Waiter", "Waiter", "Commis", "Executive Chef"],
  "Runner": ["Runner", "Waiter", "Commis", "Executive Chef"],
  "Host": ["Host", "Waiter", "Commis", "Executive Chef"],
  "Barback": ["Barback", "Bartender", "Commis", "Executive Chef"],
  "Steward": ["Steward", "Commis", "Chef de Partie", "Executive Chef"],
  "Stewarding Supervisor": ["Stewarding Supervisor", "Steward", "Sous Chef", "Executive Chef"],
  "Cost Controller": ["Cost Controller", "Restaurant Manager", "Executive Chef"],
};

function findAnchorRole(profile: CityStaffProfile, anchor: string): CityRoleSalary {
  const chain = ANCHOR_FALLBACKS[anchor] ?? [anchor, "Executive Chef"];
  for (const candidate of chain) {
    const hit = profile.roles.find(r => r.role === candidate);
    if (hit) return hit;
  }
  // Final guarantee — Executive Chef exists in every defined profile.
  return profile.roles.find(r => r.role === "Executive Chef") ?? profile.roles[0];
}

// ─────────────────────────────────────────────────────────────────────────
//  DEPARTMENT DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────

export const HOTEL_DEPARTMENTS: HotelDepartmentDef[] = [
  {
    id: "executive",
    label: "Executive Leadership",
    description: "Property-wide leadership and strategy.",
    roles: [
      { name: "General Manager",                  priority: "essential",   description: "Top property leader; owns P&L, brand standards, and guest experience.", anchorRole: "F&B Director", multiplier: 1.5 },
      { name: "Deputy General Manager / Resident Manager", priority: "recommended", description: "Second in command; covers GM and oversees daily ops.", anchorRole: "F&B Director", multiplier: 1.15 },
      { name: "Director of Operations",           priority: "recommended", description: "Coordinates rooms, F&B, and support departments.", anchorRole: "F&B Director", multiplier: 1.05 },
      { name: "Executive Assistant Manager",      priority: "optional",    description: "Senior cross-functional manager supporting the GM.", anchorRole: "F&B Director", multiplier: 0.95 },
    ],
  },
  {
    id: "front-office",
    label: "Front Office & Guest Experience",
    description: "First impressions, check-in, concierge, butlers and reservations.",
    roles: [
      { name: "Director of Rooms",                priority: "essential",   description: "Owns front office, housekeeping, and guest services.", anchorRole: "F&B Director", multiplier: 0.95 },
      { name: "Front Office Manager",             priority: "essential",   description: "Runs the front desk, scheduling and guest issue resolution.", anchorRole: "Restaurant Manager", multiplier: 1.0 },
      { name: "Assistant Front Office Manager",   priority: "recommended", description: "Supports the FOM and leads shifts.", anchorRole: "Restaurant Manager", multiplier: 0.7 },
      { name: "Front Desk Agent / Guest Services Agent", priority: "essential", description: "Check-in/out, payments, and guest requests.", anchorRole: "Waiter", multiplier: 1.1 },
      { name: "Night Manager",                    priority: "essential",   description: "Senior on-property leader during overnight shifts.", anchorRole: "Restaurant Manager", multiplier: 0.75 },
      { name: "Night Auditor",                    priority: "essential",   description: "Closes the day's books and validates room revenue overnight.", anchorRole: "Waiter", multiplier: 1.2 },
      { name: "Chef Concierge",                   priority: "recommended", description: "Lead concierge; bespoke guest arrangements and Les Clefs d'Or standards.", anchorRole: "Maître d'", multiplier: 0.95 },
      { name: "Concierge",                        priority: "recommended", description: "Restaurant bookings, transport, and tickets for guests.", anchorRole: "Maître d'", multiplier: 0.65 },
      { name: "Guest Relations Manager",          priority: "recommended", description: "Owns VIP/loyalty experience and recovery cases.", anchorRole: "Restaurant Manager", multiplier: 0.85 },
      { name: "Guest Relations Officer / Butler", priority: "recommended", description: "Personalized in-suite service for VIP guests.", anchorRole: "Waiter", multiplier: 1.3 },
      { name: "Reservations Manager",             priority: "recommended", description: "Manages booking inflow, channels, and group reservations.", anchorRole: "Restaurant Manager", multiplier: 0.75 },
      { name: "Reservations Agent",               priority: "recommended", description: "Handles individual booking calls and emails.", anchorRole: "Waiter", multiplier: 1.0 },
      { name: "Telephone Operator",               priority: "optional",    description: "Routes calls and supports in-room dining and concierge requests.", anchorRole: "Waiter", multiplier: 0.9 },
      { name: "Valet / Doorman",                  priority: "optional",    description: "Door greeting and vehicle handling.", anchorRole: "Waiter", multiplier: 0.85 },
      { name: "Bellman / Porter",                 priority: "optional",    description: "Luggage handling and room familiarization.", anchorRole: "Waiter", multiplier: 0.85 },
    ],
  },
  {
    id: "housekeeping",
    label: "Housekeeping",
    description: "Room cleanliness, public areas, and laundry.",
    roles: [
      { name: "Executive Housekeeper",            priority: "essential",   description: "Department head; standards, scheduling, and inventory.", anchorRole: "Restaurant Manager", multiplier: 0.9 },
      { name: "Assistant Executive Housekeeper",  priority: "recommended", description: "Backs up the Executive Housekeeper and runs shifts.", anchorRole: "Restaurant Manager", multiplier: 0.65 },
      { name: "Floor Supervisor / Inspector",     priority: "essential",   description: "Inspects rooms and supports attendants on assigned floors.", anchorRole: "Sous Chef", multiplier: 0.7 },
      { name: "Room Attendant",                   priority: "essential",   description: "Cleans and resets guest rooms to brand standard.", anchorRole: "Waiter", multiplier: 0.95 },
      { name: "Turndown Attendant",               priority: "recommended", description: "Evening turndown service and amenities replenishment.", anchorRole: "Waiter", multiplier: 0.9 },
      { name: "Public Area Supervisor",           priority: "recommended", description: "Oversees lobby, restrooms, corridors, and meeting spaces.", anchorRole: "Sous Chef", multiplier: 0.6 },
      { name: "Public Area Attendant",            priority: "recommended", description: "Day cleaning of all guest-facing public areas.", anchorRole: "Waiter", multiplier: 0.85 },
      { name: "Laundry Manager",                  priority: "recommended", description: "Runs in-house or outsourced laundry operations.", anchorRole: "Sous Chef", multiplier: 0.7 },
      { name: "Laundry Attendant",                priority: "optional",    description: "Washes, presses, and folds linens and uniforms.", anchorRole: "Steward", multiplier: 1.0 },
    ],
  },
  {
    id: "fb",
    label: "Food & Beverage",
    description: "Outlets, banqueting, and in-room dining management.",
    roles: [
      { name: "Director of Food & Beverage",      priority: "essential",   description: "Owns all F&B outlets, banqueting and IRD.", anchorRole: "F&B Director", multiplier: 1.0 },
      { name: "F&B Manager",                      priority: "essential",   description: "Runs day-to-day F&B operations across outlets.", anchorRole: "F&B Director", multiplier: 0.7 },
      { name: "Restaurant Manager — per outlet",  priority: "essential",   description: "Manages a single outlet's service team and P&L.", anchorRole: "Restaurant Manager", multiplier: 1.0 },
      { name: "Assistant Restaurant Manager",     priority: "recommended", description: "Supports the Restaurant Manager and leads shifts.", anchorRole: "Restaurant Manager", multiplier: 0.7 },
      { name: "Maître d'",                        priority: "recommended", description: "Floor host; reservations, VIP welcome, and guest flow.", anchorRole: "Maître d'", multiplier: 1.0 },
      { name: "Banqueting & Events Manager",      priority: "recommended", description: "Runs weddings, conferences, and group events.", anchorRole: "Restaurant Manager", multiplier: 0.95 },
      { name: "Banqueting Coordinator",           priority: "recommended", description: "Plans BEOs, room setups, and event logistics.", anchorRole: "Restaurant Manager", multiplier: 0.6 },
      { name: "In-Room Dining Manager",           priority: "recommended", description: "Owns the IRD experience, menu, and 24-hr service.", anchorRole: "Restaurant Manager", multiplier: 0.85 },
      { name: "In-Room Dining Supervisor",        priority: "optional",    description: "Leads the IRD team during shifts.", anchorRole: "Restaurant Manager", multiplier: 0.6 },
    ],
  },
  {
    id: "culinary",
    label: "Culinary",
    description: "Kitchen brigade across all outlets, banqueting, and pastry.",
    roles: [
      { name: "Executive Chef",                   priority: "essential",   description: "Property-wide culinary leader; menus, costs, and standards.", anchorRole: "Executive Chef", multiplier: 1.0 },
      { name: "Chef de Cuisine — per outlet",     priority: "essential",   description: "Runs the kitchen of a specific outlet.", anchorRole: "Chef de Cuisine", multiplier: 1.0 },
      { name: "Sous Chef",                        priority: "essential",   description: "Second in command of the kitchen; covers the chef.", anchorRole: "Sous Chef", multiplier: 1.0 },
      { name: "Chef de Partie",                   priority: "essential",   description: "Owns a station (sauce, grill, fish, etc.).", anchorRole: "Chef de Partie", multiplier: 1.0 },
      { name: "Commis Chef",                      priority: "essential",   description: "Junior cook supporting a Chef de Partie.", anchorRole: "Commis", multiplier: 1.0 },
      { name: "Pastry Chef",                      priority: "recommended", description: "Leads desserts, breads, and pastry production.", anchorRole: "Pastry Chef", multiplier: 1.0 },
      { name: "Pastry Sous Chef",                 priority: "recommended", description: "Backs up the Pastry Chef.", anchorRole: "Pastry Sous Chef", multiplier: 1.0 },
      { name: "Garde Manger",                     priority: "recommended", description: "Cold kitchen — salads, charcuterie, canapés.", anchorRole: "Chef de Partie", multiplier: 1.0 },
      { name: "Stewarding Supervisor",            priority: "essential",   description: "Manages dishwashing and back-of-house cleanliness.", anchorRole: "Stewarding Supervisor", multiplier: 1.0 },
      { name: "Steward / Dishwasher",             priority: "essential",   description: "Washes ware and supports kitchen sanitation.", anchorRole: "Steward", multiplier: 1.0 },
    ],
  },
  {
    id: "bar",
    label: "Bar & Beverage",
    description: "Bars, lobby lounge, and beverage program.",
    roles: [
      { name: "Head Bartender",                   priority: "essential",   description: "Leads the bar team and cocktail program.", anchorRole: "Head Bartender", multiplier: 1.0 },
      { name: "Sommelier",                        priority: "recommended", description: "Curates the wine list and pairings.", anchorRole: "Sommelier", multiplier: 1.0 },
      { name: "Bartender",                        priority: "essential",   description: "Mixes drinks and serves at the bar.", anchorRole: "Bartender", multiplier: 1.0 },
      { name: "Barback",                          priority: "optional",    description: "Restocks, preps garnish, supports bartenders.", anchorRole: "Barback", multiplier: 1.0 },
    ],
  },
  {
    id: "spa",
    label: "Spa & Wellness",
    description: "Spa, fitness, and wellness experiences.",
    roles: [
      { name: "Spa Director",                     priority: "recommended", description: "Owns the spa P&L, treatments, and retail.", anchorRole: "Restaurant Manager", multiplier: 0.9 },
      { name: "Spa Manager",                      priority: "recommended", description: "Day-to-day spa operations and team scheduling.", anchorRole: "Restaurant Manager", multiplier: 0.7 },
      { name: "Spa Receptionist",                 priority: "recommended", description: "Bookings, check-in, and retail at the spa.", anchorRole: "Waiter", multiplier: 0.95 },
      { name: "Therapist / Beautician",           priority: "recommended", description: "Delivers massage, facial, and body treatments.", anchorRole: "Sommelier", multiplier: 0.7 },
      { name: "Nail Technician",                  priority: "optional",    description: "Manicure and pedicure services.", anchorRole: "Waiter", multiplier: 1.0 },
      { name: "Fitness Manager",                  priority: "optional",    description: "Runs the gym and group programming.", anchorRole: "Restaurant Manager", multiplier: 0.6 },
      { name: "Personal Trainer / Fitness Instructor", priority: "optional", description: "Personal training and group classes.", anchorRole: "Sommelier", multiplier: 0.7 },
    ],
  },
  {
    id: "sales",
    label: "Sales, Marketing & Revenue",
    description: "Top-line growth, channels, and brand.",
    roles: [
      { name: "Director of Sales & Marketing",    priority: "essential",   description: "Owns commercial strategy, sales pipeline, and brand.", anchorRole: "F&B Director", multiplier: 1.0 },
      { name: "Director of Revenue Management",   priority: "essential",   description: "Pricing, forecasting, and channel mix.", anchorRole: "F&B Director", multiplier: 0.95 },
      { name: "Sales Manager",                    priority: "recommended", description: "Owns a market segment (corporate, MICE, leisure).", anchorRole: "Restaurant Manager", multiplier: 1.0 },
      { name: "Account Manager",                  priority: "recommended", description: "Manages key corporate and travel-trade accounts.", anchorRole: "Restaurant Manager", multiplier: 0.85 },
      { name: "Marketing Manager",                priority: "recommended", description: "Brand campaigns, partnerships, and PR.", anchorRole: "Restaurant Manager", multiplier: 0.85 },
      { name: "Social Media / Content Coordinator", priority: "optional", description: "Owns social channels, content, and influencer programs.", anchorRole: "Sommelier", multiplier: 0.85 },
      { name: "Reservations & Revenue Analyst",   priority: "recommended", description: "Forecasts demand and tunes pricing levers.", anchorRole: "Cost Controller", multiplier: 0.95 },
    ],
  },
  {
    id: "finance",
    label: "Finance & Administration",
    description: "Controls, AP/AR, payroll, and purchasing.",
    roles: [
      { name: "Financial Controller",             priority: "essential",   description: "Leads finance; reporting, audit, and compliance.", anchorRole: "F&B Director", multiplier: 0.95 },
      { name: "Assistant Controller",             priority: "recommended", description: "Backs up the Controller; owns close cycles.", anchorRole: "Restaurant Manager", multiplier: 1.0 },
      { name: "Cost Controller",                  priority: "recommended", description: "Owns F&B costs, inventory, and recipe costing.", anchorRole: "Cost Controller", multiplier: 1.0 },
      { name: "Accounts Payable / Receivable",    priority: "recommended", description: "Vendor payments, guest billing, and collections.", anchorRole: "Cost Controller", multiplier: 0.7 },
      { name: "Purchasing Manager",               priority: "recommended", description: "Sourcing, vendor contracts, and approvals.", anchorRole: "Restaurant Manager", multiplier: 0.85 },
      { name: "Purchasing Agent",                 priority: "optional",    description: "Day-to-day ordering and receiving.", anchorRole: "Cost Controller", multiplier: 0.6 },
      { name: "Payroll Coordinator",              priority: "optional",    description: "Runs payroll, benefits, and time records.", anchorRole: "Cost Controller", multiplier: 0.65 },
    ],
  },
  {
    id: "hr",
    label: "Human Resources",
    description: "Talent, training, and culture.",
    roles: [
      { name: "HR Director / Manager",            priority: "essential",   description: "Owns people strategy, ER, and compliance.", anchorRole: "F&B Director", multiplier: 0.85 },
      { name: "HR Coordinator",                   priority: "recommended", description: "Onboarding, records, and employee support.", anchorRole: "Restaurant Manager", multiplier: 0.65 },
      { name: "Training Manager",                 priority: "recommended", description: "Brand standards, service training, and L&D.", anchorRole: "Restaurant Manager", multiplier: 0.8 },
      { name: "Recruitment Coordinator",          priority: "optional",    description: "Sourcing, scheduling interviews, and offers.", anchorRole: "Restaurant Manager", multiplier: 0.6 },
    ],
  },
  {
    id: "engineering",
    label: "Engineering & Maintenance",
    description: "Building systems, MEP, and IT.",
    roles: [
      { name: "Chief Engineer",                   priority: "essential",   description: "Owns building, MEP, and capex projects.", anchorRole: "F&B Director", multiplier: 0.85 },
      { name: "Assistant Chief Engineer",         priority: "recommended", description: "Backs up the Chief Engineer.", anchorRole: "Restaurant Manager", multiplier: 0.85 },
      { name: "Maintenance Technician",           priority: "essential",   description: "General repairs across guest rooms and back-of-house.", anchorRole: "Sous Chef", multiplier: 0.85 },
      { name: "HVAC Technician",                  priority: "recommended", description: "Heating, ventilation, and air-conditioning systems.", anchorRole: "Sous Chef", multiplier: 0.95 },
      { name: "IT Manager",                       priority: "recommended", description: "Owns PMS, POS, networks, and cybersecurity.", anchorRole: "Restaurant Manager", multiplier: 1.0 },
      { name: "IT Support Technician",            priority: "optional",    description: "Day-to-day IT support for staff and guests.", anchorRole: "Sous Chef", multiplier: 0.75 },
    ],
  },
  {
    id: "security",
    label: "Security",
    description: "Guest, staff, and asset safety.",
    roles: [
      { name: "Director of Security",             priority: "recommended", description: "Owns property safety, investigations, and crisis response.", anchorRole: "Restaurant Manager", multiplier: 1.0 },
      { name: "Security Supervisor",              priority: "recommended", description: "Leads shifts and manages the officer team.", anchorRole: "Sous Chef", multiplier: 0.85 },
      { name: "Security Officer",                 priority: "essential",   description: "Patrols, monitors CCTV, and responds to incidents.", anchorRole: "Steward", multiplier: 1.1 },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────
//  RESOLUTION + FORMATTING
// ─────────────────────────────────────────────────────────────────────────

export interface ResolvedHotelRole {
  name: string;
  priority: HotelRolePriority;
  description: string;
  monthlyMin: number;
  monthlyMax: number;
  rangeLabel: string;
  /** Average monthly cost in city currency (used for totals). */
  monthlyAvg: number;
  suffix?: string;
}

export interface ResolvedHotelDepartment {
  id: string;
  label: string;
  description: string;
  roles: ResolvedHotelRole[];
}

/**
 * Resolve every hotel role's salary range against the active city profile.
 * Falls back to NYC if the city has no luxury profile so the planner stays
 * functional for any onboarding city.
 */
export function resolveHotelDepartments(city: string): {
  departments: ResolvedHotelDepartment[];
  profile: CityStaffProfile;
} {
  const profile =
    getCityStaffProfile(city) ??
    CITY_STAFF_DATA["new york"];

  const symbol = profile.currencySymbol;

  const departments: ResolvedHotelDepartment[] = HOTEL_DEPARTMENTS.map(dept => ({
    id: dept.id,
    label: dept.label,
    description: dept.description,
    roles: dept.roles.map(role => {
      const anchor = findAnchorRole(profile, role.anchorRole);
      const mult = role.multiplier ?? 1;
      const annualMin = anchor.annualMin * mult;
      const annualMax = anchor.annualMax * mult;
      const monthlyMin = Math.round(annualMin / 12);
      const monthlyMax = Math.round(annualMax / 12);
      const monthlyAvg = (monthlyMin + monthlyMax) / 2;
      const suffix = role.suffix ?? anchor.suffix;
      const fmt = (n: number) => `${symbol}${n.toLocaleString()}`;
      const rangeLabel = `${fmt(monthlyMin)}–${fmt(monthlyMax)}/mo${suffix ? ` ${suffix}` : ""}`;
      return {
        name: role.name,
        priority: role.priority,
        description: role.description,
        monthlyMin,
        monthlyMax,
        monthlyAvg,
        rangeLabel,
        suffix,
      };
    }),
  }));

  return { departments, profile };
}
